/**
 * One item in an ecommerce report, in the destination's own field names — its
 * recommended-event vocabulary, not ours. Every field past the identity is optional
 * because a producer omits what it cannot state truthfully: an absent key reports
 * "not supplied", while a placeholder reports a value that is wrong.
 */
export interface GtmEcommerceItem {
    item_id: string
    item_name?: string
    item_brand?: string
    item_category?: string
    item_category2?: string
    item_variant?: string
    price?: number
    quantity?: number
    index?: number
    google_business_vertical?: string
    origin?: string
    start_date?: string
    end_date?: string
    match_type?: string
}

/** The list a report happened on, and the items it carries. */
export interface GtmEcommerce {
    item_list_id: string
    item_list_name?: string
    /**
     * A field the destinations read at the REPORT's scope rather than the item's. The two
     * scopes are separate keys there, so one supplied only inside an item leaves the
     * report-scoped dimension empty; a producer that wants both states it in both places.
     */
    match_type?: string
    items: GtmEcommerceItem[]
}

export type EcommerceReportOutcome = 'reported' | 'failed'

/**
 * Report a surface's ecommerce event onto the page's event queue. Every producer goes
 * through here, so the message shape cannot drift between surfaces.
 *
 * Retracting first is what makes this a replace rather than a merge. A tag reads the
 * queue's accumulated model, which merges an object field by field, so a list pushed
 * after another would otherwise inherit every field the previous one carried and this
 * one does not — reporting one surface's list under another's item.
 *
 * Creating the queue here is load-bearing: a report that lands before the container
 * arrives is replayed by the bootstrap, so an early one is not lost.
 *
 * **Never throws.** A producer here is a page render or a click handler whose own work
 * must finish: a container-side tag throwing inside the push would otherwise turn a
 * control the visitor took for a link into a dead one. The outcome is for a caller that
 * wants to record the loss; ignoring it is a valid choice.
 */
export function reportEcommerceEvent(
    event: string,
    ecommerce: GtmEcommerce,
): EcommerceReportOutcome {
    try {
        window.dataLayer = window.dataLayer ?? []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event, ecommerce })
        return 'reported'
    } catch {
        return 'failed'
    }
}
